// src/AssetManager/VersionsDrawer.jsx
//
// Enterprise "Versions" side drawer for assets.
// - Fetches version history by querying assets with same
//   {organizationId, canonicalName, assetType}.
// - Highlights current version, lets the user select older versions.
// - Provides "+ New Version" CTA (delegated to parent).
// - Kebab per row for common actions (preview/download/etc.).
//
// This is view-only to start; "Restore as new current" can be
// wired later via a CF helper.
//
// Dependencies: MUI v5, Firebase client db, your existing
// frontend_assetManager.fetchAssetById (for signed preview).

import React from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Divider,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import AddIcon from "@mui/icons-material/Add";

// ✅ Correct relative paths from src/AssetManager/
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { fetchAssetById } from "../utils/frontend_assetManager";
import { useAuth } from "../contexts/AuthContext";
import PreviewLightbox from "./PreviewLightbox";

// Quick micro-thumb cell; signs by calling your CF via fetchAssetById.
function Thumb({ assetId, size = 32 }) {
  const [url, setUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const { currentUser } = useAuth();

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const idToken = await currentUser?.getIdToken?.();
        const out = await fetchAssetById(assetId, idToken, {
          includeMeta: false,
          prefer: "micro",
        });
        if (!dead && out?.previewUrl) setUrl(out.previewUrl);
      } catch (_) {
        /* non-fatal */
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [assetId, currentUser]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: "action.hover",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 1,
        flex: "0 0 auto",
      }}
    >
      {loading ? (
        <CircularProgress size={16} />
      ) : url ? (
        <img
          src={url}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : null}
    </Box>
  );
}

Thumb.propTypes = {
  assetId: PropTypes.string.isRequired,
  size: PropTypes.number,
};

export default function VersionsDrawer({
  open,
  onClose,
  organizationId,
  currentAsset, // { id, assetId, canonicalName, assetType, versionNumber, ... }
  onSelectVersion, // (assetDoc) => void
  onAddNewVersion, // () => void
}) {
//debug logs
 React.useEffect(() => {
   if (open) {
     console.log('[VersionsDrawer] open mount for', {
       org: organizationId,
       canonicalName: currentAsset?.canonicalName,
       assetType: currentAsset?.assetType,
       versionNumber: currentAsset?.versionNumber,
       id: currentAsset?.id || currentAsset?.assetId
     });
   }
   return () => {
     if (open) console.log('[VersionsDrawer] unmount');
   };
 }, [open]);
//end debug logs

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuRow, setMenuRow] = React.useState(null);
  const { currentUser } = useAuth();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState("");

  const openMenu = (e, row) => {
   e.stopPropagation();                // don't bubble to list item/backdrop
   setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  // Fetch all versions that match same identity tuple.
  React.useEffect(() => {
    if (!open) return;
    if (!organizationId || !currentAsset?.canonicalName) return;

    let dead = false;
    (async () => {
      setLoading(true);
     console.log('[VersionsDrawer] fetch start');
      try {
        const ref = collection(db, "assets");
        const q = query(
          ref,
          where("organizationId", "==", organizationId),
          where("canonicalName", "==", currentAsset.canonicalName),
          where("assetType", "==", currentAsset.assetType ?? null),
          orderBy("versionNumber", "desc"),
          limit(50)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!dead) setRows(list);
       console.log('[VersionsDrawer] fetch ok: rows=', list.length);


      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          "[VersionsDrawer] history fetch failed:",
          e?.message || e
        );
       console.log('[VersionsDrawer] fetch error');

        if (!dead) setRows([]);
      } finally {
        if (!dead) setLoading(false);
       console.log('[VersionsDrawer] fetch end');

      }
    })();

    return () => {
      dead = true;
    };
  }, [open, organizationId, currentAsset?.canonicalName, currentAsset?.assetType]);

  const isCurrent = (r) =>
    typeof r?.versionNumber === "number" &&
    r.versionNumber === currentAsset?.versionNumber;

  const handlePreview = async (r) => {
    try {
      // Ask the CF for a best-fit preview (grid 800 for DPR=2).
     const idToken = await currentUser?.getIdToken?.();
     const out = await fetchAssetById(r.assetId || r.id, idToken, { dpr: 2 });
      setPreviewUrl(out?.previewUrl || "");
      setPreviewOpen(true);
    } catch (e) {
      // non-fatal; you could show a toast
    } finally {
      closeMenu(); // just close the menu; drawer stays open
    }
  };


  const handleDownload = async (r) => {
    try {
      const idToken = await currentUser?.getIdToken?.();
     const out = await fetchAssetById(
       r.assetId || r.id,
       idToken,
       { prefer: "original", includeMeta: true }
     );

      const href = out?.previewUrl;
      if (href) {
        const a = document.createElement("a");
        a.href = href;
        a.download = r.name || `asset_${r.assetId || r.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[VersionsDrawer] download failed:", e?.message || e);
    } finally {
      closeMenu();
    }
  };

  const paperRef = React.useRef(null);
  const contentRef = React.useRef(null);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
     PaperProps={{
       ref: paperRef,
       // Prevent any interaction inside the Drawer from bubbling to the Dialog/page beneath.
       onClick: (e) => { e.stopPropagation(); },
       onMouseDownCapture: (e) => { e.stopPropagation(); },
       onKeyDownCapture: (e) => { e.stopPropagation(); },

       sx: {
         width: 360,
         display: "flex",
         borderRadius: 0,  // no rounded look that reads like a gap
         m: 0,             // ignore any theme margin on Paper
         p: 0,             // no interior padding added by theme

         /* ─────────────────────────────────────────────
            LEFT EDGE SEPARATION (MUI v5 best practice)
            - a true 1px hairline divider
            - a subtle left-cast shadow so it never gets lost on white pages
            - no background image to avoid elevation overlays
            ───────────────────────────────────────────── */
         borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
         boxShadow: (theme) => `-12px 0 32px rgba(0,0,0,0.14), -4px 0 12px rgba(0,0,0,0.10)`,
         backgroundImage: 'none',
       }
     }}


 ModalProps={{
   keepMounted: true,
   hideBackdrop: true,   // ← key: do NOT add a second dim layer
 }}

    >
      {/* Header */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Versions
        </Typography>
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      {/* New Version CTA */}
      <Box sx={{ p: 2, pt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onAddNewVersion?.()}
        >
          New version
        </Button>
      </Box>

      <Divider />

      {/* List */}
      <Box ref={contentRef} sx={{ flex: 1, overflow: "auto", position: 'relative' }}>

        {loading ? (
          <Box
            sx={{
              py: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2 }}>
            No versions found.
          </Typography>
        ) : (
          <List dense disablePadding>
            {rows.map((r) => {
              const secondary = [
                r.versionNumber != null ? `v${r.versionNumber}` : null,
                r.updatedAt?.toDate?.()?.toLocaleString?.() ||
                  r.createdAt?.toDate?.()?.toLocaleString?.() ||
                  null,
                r.dimensions?.width && r.dimensions?.height
                  ? `${r.dimensions.width}×${r.dimensions.height}`
                  : null,
                r.size != null ? `${r.size} KB` : null,
              ]
                .filter(Boolean)
                .join(" · ");

              return (
                <ListItemButton
                  key={r.id}
                  selected={isCurrent(r)}
                  onClick={() => onSelectVersion?.(r)}
                  sx={{ px: 2 }}
                >
                  <Thumb assetId={r.assetId || r.id} />
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {r.name || currentAsset?.name || "Asset"}
                        </Typography>
                        {isCurrent(r) && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: "10px",
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: "primary.light",
                              color: "primary.contrastText",
                            }}
                          >
                            Current
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={secondary}
                    secondaryTypographyProps={{ sx: { fontSize: "11px" } }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={(e) => openMenu(e, r)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      {/* Row menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        disablePortal
        container={contentRef.current}
        anchorOrigin={{ vertical: 'top',    horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 'auto',
            minWidth: '12ch',
            maxWidth: '20ch',
            p: 0,
            borderRadius: 1,
            boxShadow: 3,
            flex: '0 0 auto',        // ← prevent flex stretching just in case
            alignSelf: 'flex-start',
          },
        }}
        MenuListProps={{ dense: true, disablePadding: false }}

      >
		   <MenuItem onClick={(e) => { e.stopPropagation(); handlePreview(menuRow); }}>
		     Preview
		   </MenuItem>
		   <MenuItem
		     onClick={(e) => { e.stopPropagation(); handleDownload(menuRow); }}
		   >
          <CloudDownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Download
        </MenuItem>
        {/* Future:
        <MenuItem disabled onClick={handleRestore}>Restore as new</MenuItem>
        <MenuItem disabled onClick={handleCompare}>Compare…</MenuItem>
        <MenuItem disabled onClick={handleDelete}>Delete…</MenuItem>
        */}
      </Menu>
     {/* Fullscreen preview (stays on top; drawer remains open behind) */}
     <PreviewLightbox
       open={previewOpen}
       onClose={() => setPreviewOpen(false)}
       url={previewUrl}
       alt={menuRow?.name || currentAsset?.name || "Asset preview"}
     />


    </Drawer>
  );
}

VersionsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  currentAsset: PropTypes.object.isRequired,
  onSelectVersion: PropTypes.func,
  onAddNewVersion: PropTypes.func,
};
